'use client'

import { AlertTriangle, CreditCardIcon, Loader2Icon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'

import {
  SettingsCard,
  SettingsCardHeader,
  SettingsRow,
  SettingsRowGroup,
} from '@/components/dialog/settings/settings-card'
import { Button } from '@/components/ui/button'
import BuyCreditsDialog from '@/features/settings/billing/credits/buy-credits-dialog'
import { nextWeeklyReset } from '@/features/settings/billing/credits/lib/credits'
import type { ApiBillingView } from '@/features/settings/billing/credits/lib/mappers'
import { useCreditFormat } from '@/features/settings/billing/credits/use-credit-format'
import { cn } from '@/lib/utils'
import { formatFirstLetterToUpperCase } from '@/lib/utils/format-util'

export default function CreditOverviewCard({
  billing,
  processing,
}: {
  billing: ApiBillingView
  /** A Stripe return is waiting for its credits to land. */
  processing: boolean
}) {
  const t = useTranslations('settings.billing.credits')
  const locale = useLocale()
  const { credits } = useCreditFormat()
  const [buyOpen, setBuyOpen] = useState(false)

  const { balance, spentThisWeek, settings, creditPrice } = billing
  const limit = settings.spendLimitWeekly
  const usedPct = limit ? Math.min(100, (spentThisWeek / limit) * 100) : 0
  // The week is a UTC week for everyone; showing it in local time would misstate the reset.
  const resetsAt = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(nextWeeklyReset(new Date()))

  // The balance status line under the Balance row: why it's empty/zero/negative, if it is.
  const balanceNote =
    balance === undefined
      ? t('balance.emptyHint')
      : balance === 0
        ? t('balance.zero')
        : balance < 0
          ? t('balance.negative')
          : null

  return (
    <SettingsCard id="api-credit-buy">
      {/* Header + its one action (Buy), then label/value rows — the same grammar as the
          plan card above and the other Settings tabs. */}
      <div className="flex w-full items-center justify-between gap-4">
        <SettingsCardHeader title={t('title')} description={t('description')} />
        <Button variant="cta" size="sm" disabled={!creditPrice} onClick={() => setBuyOpen(true)}>
          {t('buy.button')}
        </Button>
      </div>

      {processing && (
        <p className="text-foreground/70 flex items-center gap-2 text-sm tracking-tight">
          <Loader2Icon className="size-4 animate-spin" />
          {t('return.processing')}
        </p>
      )}

      <SettingsRowGroup>
        <SettingsRow
          label={t('balance.label')}
          description={
            balanceNote && (
              <span
                className={cn(
                  'flex items-start gap-1.5',
                  balance !== undefined && balance <= 0 && 'text-ox-gold'
                )}
              >
                {balance === 0 && <AlertTriangle className="mt-px size-3.5 shrink-0" />}
                {balanceNote}
              </span>
            )
          }
          control={
            <span
              className={cn(
                'text-sm font-semibold tracking-tight tabular-nums',
                balance !== undefined && balance <= 0 ? 'text-ox-gold' : 'text-foreground'
              )}
            >
              {balance === undefined
                ? t('balance.empty')
                : t('balance.value', { credits: credits(balance) })}
            </span>
          }
        />
        <SettingsRow
          label={t('week.charged')}
          description={t('week.resets', { date: resetsAt })}
          control={
            <div className="flex w-40 flex-col items-end gap-1.5">
              <span className="text-foreground text-sm tabular-nums">
                {limit
                  ? t('week.ofLimit', { used: credits(spentThisWeek), limit: credits(limit) })
                  : t('week.noLimit', { used: credits(spentThisWeek) })}
              </span>
              {limit !== undefined && (
                <div className="bg-foreground/10 h-1 w-full overflow-hidden">
                  <div
                    className={cn('h-full', usedPct >= 100 ? 'bg-ox-gold' : 'bg-foreground')}
                    style={{ width: `${usedPct}%` }}
                  />
                </div>
              )}
            </div>
          }
        />
      </SettingsRowGroup>

      <p className="text-foreground/60 flex items-center gap-2 text-xs tracking-tight">
        <CreditCardIcon className="size-3.5 shrink-0" />
        {billing.cardLast4
          ? t('card.saved', {
              brand: formatFirstLetterToUpperCase(billing.cardBrand ?? ''),
              last4: billing.cardLast4,
            })
          : t('card.none')}
      </p>
      {!creditPrice && (
        <p className="text-foreground/60 text-xs tracking-tight">{t('buy.unavailable')}</p>
      )}

      {creditPrice && (
        <BuyCreditsDialog
          open={buyOpen}
          onOpenChange={setBuyOpen}
          price={creditPrice}
          grantedTotal={billing.grantedTotal}
        />
      )}
    </SettingsCard>
  )
}
